import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Checkbox as RebaseCheckbox,
    Chip as RebaseChip,
    CircularProgress as RebaseCircularProgress,
    FilterChip as RebaseFilterChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    cardClickableMixin,
    defaultBorderMixin
} from "@rebasepro/ui";

import {
    callFunction,
    ENTITY_LABEL,
    threadTitle,
    type LinkedEntityType,
    type MessageRow,
    type ThreadRow
} from "./_email_shared";
import {
    employerFromAddress,
    employerKey,
    isAnonymousCompanyName,
    preferredEmployerName
} from "../employer_identity";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Checkbox = RebaseCheckbox as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgress = RebaseCircularProgress as React.ComponentType<any>;
const FilterChip = RebaseFilterChip as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * Saying what a conversation is about — or, far more often, who it is from.
 *
 * The inbox has always had a "Sin asignar" tab presented as a queue — every
 * thread the address-matching could not attach to anybody — and for a long
 * time the only thing to do about one was to search for a record that already
 * existed. On 2026-09-05 that queue held a hundred threads, and sixty-five of
 * them were people applying to a vacancy by email: not in `talents`, never
 * would be, because they wrote instead of filling the questionnaire. The
 * picker asked the operator to find a candidate that did not exist and had no
 * way to create one. Two-thirds of the queue could not be completed.
 *
 * ── What this does now ───────────────────────────────────────────────
 *
 * For a person nobody has a record of, the first thing offered is to *create*
 * the record from what the email already says: the name on the From header,
 * the address they wrote from, and — when they attached one — their CV, read
 * by the same `extract-cv` function "Importar CV" uses. When the subject named
 * a vacancy (see email-vacancy.ts), the candidature is registered in the same
 * step, because that is what the person was doing when they wrote.
 *
 * The employer side already worked this way ("Crear X y asignarla"); this is
 * the candidate half of the same idea.
 *
 * ── Why the record is written here rather than in the form ───────────
 *
 * "Importar CV" opens the new-talent form pre-filled and writes nothing until
 * the admin saves, because a model's reading of a stranger's CV deserves a
 * look first. That look happens here too — the extraction is shown before
 * anything is created — but the create *is* the click, because the whole
 * point of this screen is to turn a hundred threads into records without a
 * hundred round-trips through a form. Every field stays editable on the
 * record, `origin: 'email'` says how it got there, and the link to the record
 * is the first thing shown afterwards.
 *
 * ── Searching, not browsing ──────────────────────────────────────────
 *
 * Three hundred candidates and a hundred and seventy companies do not fit in a
 * dropdown, so this is a search box against whichever kind was chosen. The
 * kind opens on what the address implies — a freemail address is a person, a
 * corporate one is an employer — and the box is seeded with the sender's name,
 * so "search for this person" is usually "confirm this is them".
 *
 * ── Why the employer search is not a table lookup ────────────────────
 *
 * A prospect row only exists once somebody has acted on that employer, so a
 * search of `company_prospects` alone returned nothing for exactly the threads
 * that needed assigning. The employer search reads three places and folds them
 * onto one identity — the prospect rows that exist, the scrape queue, and the
 * brand directory — and linking to an employer with no row *creates* it, as
 * `inbound`.
 */

type Kind = Exclude<LinkedEntityType, "none" | "application">;

const KINDS: { id: Kind; label: string }[] = [
    { id: "talent", label: ENTITY_LABEL.talent },
    { id: "company", label: ENTITY_LABEL.company },
    { id: "prospect", label: ENTITY_LABEL.prospect }
];

/** The per-kind row search, for the two kinds that are a plain table lookup. */
const SIMPLE_KINDS: Record<"talent" | "company", { collection: string; nameField: string; searchFields: string[] }> = {
    talent: { collection: "talents", nameField: "full_name", searchFields: ["full_name", "email"] },
    company: { collection: "companies", nameField: "name", searchFields: ["name"] }
};

interface Candidate {
    id: string;
    name: string;
    detail?: string | null;
    employerKey?: string | null;
    website?: string | null;
    /**
     * Whether a `company_prospects` row already exists for this employer.
     *
     * False for most results and that is the point: they are employers the
     * scrape queue knows about that nobody has touched. Linking one creates the
     * row, which is the only way the "Sin asignar" queue can ever be emptied.
     */
    hasRow?: boolean;
}

/** The vacancy a thread names, as the inbox resolved it. */
export interface ThreadVacancy {
    id: string;
    title: string;
    company_id?: string | null;
}

/** What the picker did, so the inbox can say so and point at the record. */
export interface LinkOutcome {
    kind: Kind | "none";
    /** The candidate the thread now belongs to, when it is a candidate. */
    talentId?: string;
    talentName?: string;
    /** True when that candidate was created just now, from this thread. */
    createdTalent?: boolean;
    /** The candidature registered, when one was. */
    applicationId?: string;
}

/** Long enough that a search means something, short enough to be quick. */
const MIN_QUERY = 2;
const RESULTS = 8;
/** Offers are read wide and folded to employers, so the page has to be generous. */
const OFFER_SCAN = 60;

/**
 * What the backend is doing while the operator waits. Estimates on a timer,
 * not progress reported by the server — the same captions "Importar CV" shows,
 * for the same reason: a wait with no caption reads as a hang.
 */
const PHASES = [
    { after: 0, label: "Descargando el CV del buzón…" },
    { after: 3, label: "Leyendo el CV con IA…" },
    { after: 25, label: "Casi listo…" }
];

/** A candidate or a company: one table, one or two fields, nothing derived. */
async function searchRows(data: any, kind: "talent" | "company", term: string): Promise<Candidate[]> {
    const spec = SIMPLE_KINDS[kind];
    const pages = await Promise.all(spec.searchFields.map(field =>
        data.collection(spec.collection)
            .find({ where: { [field]: ["ilike", `%${term}%`] }, limit: RESULTS })
            .catch(() => undefined)
    ));
    const seen = new Set<string>();
    const found: Candidate[] = [];
    for (const page of pages) {
        for (const row of (page?.data ?? []) as Record<string, any>[]) {
            const id = String(row.id ?? "");
            if (!id || seen.has(id)) continue;
            seen.add(id);
            found.push({
                id,
                name: String(row[spec.nameField] ?? "(sin nombre)"),
                detail: row.email ?? row.domain ?? null
            });
        }
    }
    return found;
}

/**
 * An employer, from wherever this database happens to know about them.
 *
 * Three sources folded onto one `employer_key`: the outreach rows that exist,
 * the scrape queue (which is where most employers live and where none of them
 * has a row yet), and the brand directory. A hit in the first is the best answer
 * because it carries the name somebody already chose for this account; the other
 * two are employers that can be linked and will be created on the way.
 */
async function searchEmployers(data: any, term: string): Promise<Candidate[]> {
    const like = `%${term}%`;
    const [prospects, offers, companies] = await Promise.all([
        data.collection("company-prospects")
            .find({ where: { employer_name: ["ilike", like] }, limit: RESULTS }).catch(() => undefined),
        data.collection("scraped-jobs")
            .find({ where: { company_name: ["ilike", like] }, limit: OFFER_SCAN }).catch(() => undefined),
        data.collection("companies")
            .find({ where: { name: ["ilike", like] }, limit: RESULTS }).catch(() => undefined)
    ]);

    const byKey = new Map<string, Candidate>();

    for (const row of (prospects?.data ?? []) as Record<string, any>[]) {
        const key = String(row.employer_key ?? "");
        if (!key) continue;
        byKey.set(key, {
            id: key,
            name: String(row.employer_name ?? key),
            detail: row.employer_website ?? "ya está en la lista",
            employerKey: key,
            website: row.employer_website ?? null,
            hasRow: true
        });
    }

    // Several offers per employer is the normal case, so the names are collected
    // and `preferredEmployerName` picks the spelling — the same choice the
    // prospects screen makes when it groups the same rows.
    const names = new Map<string, string[]>();
    const sites = new Map<string, string>();
    for (const row of (offers?.data ?? []) as Record<string, any>[]) {
        if (row.status === "rejected" || isAnonymousCompanyName(row.company_name)) continue;
        const key = employerKey(row.company_name);
        if (!key || byKey.has(key)) continue;
        const list = names.get(key);
        if (list) list.push(String(row.company_name ?? "")); else names.set(key, [String(row.company_name ?? "")]);
        if (row.employer_website && !sites.has(key)) sites.set(key, String(row.employer_website));
    }
    for (const [key, spellings] of names) {
        byKey.set(key, {
            id: key,
            name: preferredEmployerName(spellings),
            detail: sites.get(key) ?? "la vimos publicando empleo; todavía no tiene ficha",
            employerKey: key,
            website: sites.get(key) ?? null,
            hasRow: false
        });
    }

    for (const row of (companies?.data ?? []) as Record<string, any>[]) {
        const key = String(row.employer_key ?? "") || employerKey(row.name);
        if (!key || byKey.has(key)) continue;
        byKey.set(key, {
            id: key,
            name: String(row.name ?? key),
            detail: row.domain ?? "del directorio de empresas",
            employerKey: key,
            website: row.website ?? (row.domain ? `https://${row.domain}` : null),
            hasRow: false
        });
    }

    return [...byKey.values()];
}

/**
 * Who wrote, as the message headers spell them.
 *
 * The first inbound message is the one that started the conversation on their
 * side; its From header carries the name they use, which a search box seeded
 * with the local part of the address ("valecastrojofre") could only guess at.
 */
function senderOf(thread: ThreadRow, messages: MessageRow[]): { email: string; name: string | null } | null {
    const first = messages.find(m => m.direction === "inbound" && m.from_email);
    if (first?.from_email) return { email: first.from_email, name: (first.from_name ?? "").trim() || null };
    const address = (thread.participants ?? [])[0];
    return address ? { email: address, name: null } : null;
}

/** The first PDF anybody attached on their side, which in this mailbox is a CV. */
function cvOf(messages: MessageRow[]): { messageId: string; name: string } | null {
    for (const message of messages) {
        if (message.direction !== "inbound") continue;
        const name = (message.attachment_names ?? []).find(n => /\.pdf$/i.test(n));
        if (name) return { messageId: message.id, name };
    }
    return null;
}

/** "ana.garcia" → "Ana Garcia": the fallback when the header carried no name. */
function nameFromAddress(email: string): string {
    return (email.split("@")[0] ?? "")
        .replace(/[._-]+/g, " ")
        .replace(/\d+/g, " ")
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());
}

/** A blob as bare base64, which is what `extract-cv` wants. */
function toBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("No se pudo leer el adjunto"));
        reader.onload = () => {
            const result = String(reader.result ?? "");
            const comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(blob);
    });
}

function cryptoRandomId(): string {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * The candidature for this person and this vacancy, created unless it exists.
 *
 * `source: 'email'` and no account: the person wrote, an operator recorded it.
 * The lookup first, because `talent_applications` is only unique on the
 * account id, which an email-registered application does not have.
 */
async function ensureApplication(data: any, talentId: string, vacancy: ThreadVacancy): Promise<string> {
    const existing = await data.collection("talent-applications").find({
        where: { talent_id: ["==", talentId], job_id: ["==", vacancy.id] },
        limit: 1
    }).catch(() => undefined);
    const found = ((existing?.data ?? []) as Record<string, any>[])[0];
    if (found?.id) return String(found.id);

    const created = await data.collection("talent-applications").create({
        job_id: vacancy.id,
        company_id: vacancy.company_id ?? null,
        talent_id: talentId,
        status: "applied",
        source: "email"
    });
    const row = ((created as any)?.data ?? created) as Record<string, any>;
    if (!row?.id) throw new Error("No se pudo registrar la candidatura");
    return String(row.id);
}

export function ThreadLinkPicker({
    thread,
    messages,
    vacancy,
    client,
    onLinked,
    onCancel
}: {
    thread: ThreadRow;
    /** The thread's messages, already loaded by the inbox: the sender's name and CV live in them. */
    messages: MessageRow[];
    /** The vacancy the subject names, resolved by the inbox; null when it names none. */
    vacancy: ThreadVacancy | null;
    client: any;
    onLinked: (outcome: LinkOutcome) => Promise<void> | void;
    onCancel: () => void;
}) {
    const data = client?.data;

    const sender = useMemo(() => senderOf(thread, messages), [thread, messages]);
    const cv = useMemo(() => cvOf(messages), [messages]);

    /** The employer this thread's sender implies, when no record names them. */
    const fromAddress = useMemo(() => employerFromAddress(sender?.email), [sender?.email]);

    /**
     * Open on what the address implies.
     *
     * A corporate domain is an employer until proven otherwise; a freemail
     * address is a person. The linker already caught every corporate sender
     * whose company is on record, so a corporate sender still unassigned is
     * one to *add* to the prospect list — which is what the prospect tab does.
     */
    const [kind, setKind] = useState<Kind>(() => (fromAddress ? "prospect" : "talent"));
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<Candidate[]>([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Whether to register the candidature along with the link. On by default
    // when the subject named a vacancy: that is what the person was doing.
    const [registerApplication, setRegisterApplication] = useState(Boolean(vacancy));

    // ── The create-from-this-email path ──────────────────────────────
    const [reading, setReading] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    /** What `extract-cv` read, shown before anything is written. Null until read. */
    const [extracted, setExtracted] = useState<Record<string, any> | null>(null);
    /** Where the PDF was stored so the record keeps it; null when that failed. */
    const [storedKey, setStoredKey] = useState<string | null>(null);
    const [cvError, setCvError] = useState("");

    const spec = useMemo(() => KINDS.find(k => k.id === kind)!, [kind]);

    useEffect(() => {
        setKind(fromAddress ? "prospect" : "talent");
        setRegisterApplication(Boolean(vacancy));
        setExtracted(null);
        setStoredKey(null);
        setCvError("");
    }, [thread.id, fromAddress, vacancy]);

    /**
     * Seed the search with the best name we have for whichever kind is open:
     * the sender's own name for a person, the domain's for an employer. Turns
     * "search for this person" into "confirm this is them" about half the time.
     */
    useEffect(() => {
        if (kind === "prospect" && fromAddress) {
            setQuery(fromAddress.name);
            return;
        }
        if (!sender) { setQuery(""); return; }
        setQuery(sender.name ?? nameFromAddress(sender.email));
    }, [thread.id, kind, fromAddress, sender]);

    useEffect(() => {
        if (!reading) { setElapsed(0); return; }
        const started = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [reading]);

    const phaseLabel = [...PHASES].reverse().find(p => elapsed >= p.after)?.label ?? PHASES[0].label;

    useEffect(() => {
        const term = query.trim();
        if (!data || term.length < MIN_QUERY) {
            setResults([]);
            return;
        }
        let live = true;
        setSearching(true);
        // Debounced: a search per keystroke against 300 rows is a request per
        // keystroke, and the answer only matters once somebody stops typing.
        const timer = setTimeout(async () => {
            try {
                const found = kind === "prospect"
                    ? await searchEmployers(data, term)
                    : await searchRows(data, kind, term);
                if (!live) return;
                setResults(found.slice(0, RESULTS));
            } finally {
                if (live) setSearching(false);
            }
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [data, kind, query]);

    /**
     * Whether the sender is already one of the results — in which case the
     * right move is the row, not a duplicate. Matched on address, which is the
     * one thing both sides spell the same.
     */
    const senderAlreadyListed = useMemo(
        () => kind === "talent" && Boolean(sender) && results.some(r => (r.detail ?? "").toLowerCase() === sender!.email),
        [kind, sender, results]
    );

    const link = useCallback(async (choice: Candidate) => {
        if (!data) return;
        setSaving(true);
        setError("");
        try {
            // An employer with no outreach row gets one here, and this is the
            // step that turns "Sin asignar" from a number that only goes up into
            // a queue somebody can empty. `inbound`, because the evidence for
            // creating it is that they wrote to us — which is also why it will
            // sort ahead of every cold employer in the work queue.
            if (kind === "prospect" && choice.employerKey && !choice.hasRow) {
                try {
                    await data.collection("company-prospects").create({
                        employer_key: choice.employerKey,
                        employer_name: choice.name,
                        employer_website: choice.website ?? null,
                        source: "inbound",
                        source_detail: `Correo: "${threadTitle(thread)}"`
                    });
                } catch (err: any) {
                    // A row created by somebody else between the search and the
                    // click loses the unique key, and that is fine: the row we
                    // wanted exists, which is all the link below needs. Anything
                    // else is a real failure and must not be swallowed.
                    if (!/unique|duplicate|already exists/i.test(String(err?.message ?? ""))) throw err;
                }
            }

            // The candidature, when the subject named a vacancy and the
            // operator left the box ticked. Before the thread write, so a
            // thread that says `application_id` always points at a row.
            let applicationId: string | undefined;
            if (kind === "talent" && vacancy && registerApplication) {
                applicationId = await ensureApplication(data, choice.id, vacancy);
            }

            await data.collection("email-threads").update(thread.id, {
                entity_type: kind,
                // A prospect is addressed by its employer key; the other two by
                // their row id. The database's CHECK enforces exactly this, so
                // getting it wrong here is a rejected write rather than a bad row.
                entity_id: kind === "prospect" ? null : choice.id,
                employer_key: kind === "prospect" ? (choice.employerKey ?? choice.id) : null,
                // The whole point: a person said so, and ingest will not
                // second-guess it on the next poll.
                linked_by: "manual",
                ...(applicationId ? { application_id: applicationId } : {})
                // `labels_signature` is deliberately left alone. It is the
                // record of what Gmail was last told, and clearing it would
                // throw away the only thing that knows which labels to take
                // *off* — the sync diffs against it. A changed assignment
                // changes the desired set, which is what triggers the rewrite;
                // and reassigning within the same group (one candidate to
                // another) now correctly writes nothing at all.
            });
            await onLinked({
                kind,
                ...(kind === "talent" ? { talentId: choice.id, talentName: choice.name } : {}),
                applicationId
            });
        } catch (err: any) {
            setError(err?.message || "No se pudo conectar esta conversación con esa ficha");
        } finally {
            setSaving(false);
        }
    }, [data, kind, thread, vacancy, registerApplication, onLinked]);

    /**
     * Read the CV they attached, and show what it says before creating anything.
     *
     * The PDF is fetched from the mailbox through the same endpoint the reader's
     * download button uses — nothing was ever stored, see functions/attachment.ts
     * — and handed to `extract-cv`, which returns a draft of the record. It is
     * also put in storage under `cv-imports/`, so the record keeps the document
     * it was built from; if that upload fails the extraction still stands, as
     * it does in "Importar CV".
     */
    const readCv = useCallback(async () => {
        if (!cv || !client) return;
        setReading(true);
        setCvError("");
        try {
            const token = await client?.resolveToken?.();
            const base = `${client?.baseUrl ?? ""}${client?.apiPath ?? "/api"}`;
            const url = `${base}/functions/attachment?message=${encodeURIComponent(cv.messageId)}&name=${encodeURIComponent(cv.name)}`;
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                throw new Error((json as any)?.error || `No se pudo descargar el CV (${res.status})`);
            }
            const blob = await res.blob();
            const b64 = await toBase64(blob);

            const result = await callFunction<{ ok: boolean; values: Record<string, any> }>(
                client, "extract-cv", { filename: cv.name, mimeType: "application/pdf", data: b64 }
            );
            if (!result?.ok || !result.values) throw new Error("No se pudo leer el CV");

            let key: string | null = null;
            try {
                const file = new File([blob], cv.name, { type: "application/pdf" });
                const stored = await client.storage.putObject({
                    file,
                    key: `cv-imports/${cryptoRandomId()}/${cv.name}`,
                    public: false
                });
                key = stored?.key ?? null;
            } catch (uploadError) {
                console.warn("CV upload failed, continuing with the extraction:", uploadError);
            }
            setStoredKey(key);
            setExtracted(result.values);
        } catch (err: any) {
            setCvError(err?.message || "No se pudo leer el CV.");
        } finally {
            setReading(false);
        }
    }, [cv, client]);

    /**
     * The record, from the email.
     *
     * The address is the one they wrote from, whatever the CV says: it is the
     * address a reply reaches. The name is the header's, unless the CV read one
     * and the header had none. Everything else is the extraction, when there was
     * one, and blank otherwise — a blank is always right, see extract-cv.ts.
     */
    const createTalent = useCallback(async () => {
        if (!data || !sender) return;
        setSaving(true);
        setError("");
        try {
            const values: Record<string, unknown> = {
                ...(extracted ?? {}),
                full_name: sender.name || extracted?.full_name || nameFromAddress(sender.email),
                email: sender.email,
                // How the record came to exist. Distinct from `cv_import`: this
                // person wrote to us, and contacting them about the vacancy
                // they named is the opposite of the mistake `origin` prevents.
                origin: "email",
                lead_status: "new",
                // They sent their data to apply. That is not accepting a
                // privacy policy, and the record says so rather than claiming
                // a consent nobody gave; the questionnaire flow is where that
                // box gets ticked by the person it belongs to.
                privacy_accepted: false,
                ...(storedKey ? { cv_url: storedKey } : {})
            };
            const created = await data.collection("talents").create(values);
            const row = ((created as any)?.data ?? created) as Record<string, any>;
            const talentId = String(row?.id ?? "");
            if (!talentId) throw new Error("No se pudo crear el candidato");

            let applicationId: string | undefined;
            if (vacancy && registerApplication) {
                applicationId = await ensureApplication(data, talentId, vacancy);
            }

            await data.collection("email-threads").update(thread.id, {
                entity_type: "talent",
                entity_id: talentId,
                employer_key: null,
                linked_by: "manual",
                ...(applicationId ? { application_id: applicationId } : {})
            });

            await onLinked({
                kind: "talent",
                talentId,
                talentName: String(values.full_name),
                createdTalent: true,
                applicationId
            });
        } catch (err: any) {
            setError(err?.message || "No se pudo crear el candidato");
        } finally {
            setSaving(false);
        }
    }, [data, sender, extracted, storedKey, vacancy, registerApplication, thread.id, onLinked]);

    const unlink = useCallback(async () => {
        if (!data) return;
        setSaving(true);
        try {
            await data.collection("email-threads").update(thread.id, {
                entity_type: "none",
                entity_id: null,
                employer_key: null,
                contact_id: null,
                // The candidature, if one was registered, stays: it is a fact
                // about the person and the vacancy, not about this thread's
                // filing. Only the pointer from here is dropped.
                application_id: null,
                // Back to `auto`, so the matcher may have another go — unlinking
                // is "you were wrong", not "never try again".
                linked_by: "auto"
                // Kept, so the next sync can see what to remove. Clearing it
                // here is what used to leave an unlinked conversation wearing
                // `SustenTalent/Candidatos` in Gmail forever.
            });
            await onLinked({ kind: "none" });
        } catch (err: any) {
            setError(err?.message || "No se pudo desconectar la conversación de esta ficha");
        } finally {
            setSaving(false);
        }
    }, [data, thread.id, onLinked]);

    const busy = saving || reading;

    return (
        <Card className={cls("p-3 flex flex-col gap-3", defaultBorderMixin)}>
            <Typography variant="subtitle2" component="h3">¿De quién es esta conversación?</Typography>

            <div className="flex gap-1.5 flex-wrap">
                {KINDS.map(k => (
                    <FilterChip key={k.id} active={kind === k.id} onClick={() => setKind(k.id)} disabled={busy}>
                        {k.label}
                    </FilterChip>
                ))}
            </div>

            {/*
                The candidature, offered once and honoured by both paths below.
                Ticked by default because the subject named the vacancy, and a
                person who writes "Postulación – Especialista Senior…" is
                applying; the operator unticks it for the one who was asking
                about something else.
            */}
            {kind === "talent" && vacancy && (
                <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                        size="small"
                        checked={registerApplication}
                        onCheckedChange={(next: boolean) => setRegisterApplication(Boolean(next))}
                        disabled={busy}
                    />
                    <Typography variant="body2">
                        Registrar la candidatura a «{vacancy.title}»
                    </Typography>
                </label>
            )}

            {/*
                The person nobody has a record of — which, for this mailbox, is
                most of them. Offered above the search rather than below its
                empty result, because for an application from a stranger it is
                the answer, not the fallback.
            */}
            {kind === "talent" && sender && (thread.entity_type ?? "none") === "none" && !senderAlreadyListed && (
                <Card className={cls("p-3 flex flex-col gap-2", defaultBorderMixin)}>
                    <Typography variant="body2" className="font-medium">
                        Crear candidato desde este correo
                    </Typography>
                    <Typography variant="body2" color="secondary">
                        {sender.name ?? nameFromAddress(sender.email)} · {sender.email}
                        {cv ? ` · CV: ${cv.name}` : " · sin CV adjunto"}
                    </Typography>

                    {reading && (
                        <div className="flex items-center gap-3">
                            <CircularProgress size="smallest" />
                            <Typography variant="body2" color="secondary">
                                {phaseLabel} · {elapsed}s · no se ha creado nada todavía
                            </Typography>
                        </div>
                    )}

                    {cvError && <Alert color="warning" size="small">{cvError}</Alert>}

                    {extracted && (
                        <div className="flex flex-col gap-1.5">
                            <Typography variant="body2" color="secondary">
                                Lo que dice el CV. Lo que no aparecía se ha dejado vacío a propósito
                                {storedKey ? "; el PDF quedará adjunto a la ficha." : ". No se pudo guardar el PDF; los datos sí."}
                            </Typography>
                            <Typography variant="body2">
                                {[extracted.phone, extracted.location].filter(Boolean).join(" · ") || "sin teléfono ni ubicación"}
                            </Typography>
                            <div className="flex flex-wrap gap-1">
                                {[
                                    ...(Array.isArray(extracted.questionnaire?.degree) ? extracted.questionnaire.degree : []),
                                    ...(Array.isArray(extracted.questionnaire?.certifications) ? extracted.questionnaire.certifications : []),
                                    ...(Array.isArray(extracted.questionnaire?.skills) ? extracted.questionnaire.skills : [])
                                ].slice(0, 8).map((v: string, i: number) => (
                                    <Chip key={`${v}-${i}`} size="small" colorScheme="grayLighter">{v}</Chip>
                                ))}
                            </div>
                            {extracted.professional_objective && (
                                <Typography variant="body2" className="opacity-80">
                                    {String(extracted.professional_objective).slice(0, 220)}
                                </Typography>
                            )}
                        </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                        {cv && !extracted && (
                            <Button size="small" variant="filled" disabled={busy} onClick={() => void readCv()}>
                                Leer el CV
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant={cv && !extracted ? "outlined" : "filled"}
                            disabled={busy}
                            onClick={() => void createTalent()}
                        >
                            {extracted
                                ? (vacancy && registerApplication ? "Crear candidato y candidatura" : "Crear candidato")
                                : cv
                                    ? "Crear sin leer el CV"
                                    : (vacancy && registerApplication ? "Crear candidato y candidatura" : "Crear candidato")}
                        </Button>
                    </div>
                </Card>
            )}

            <TextField
                size="small"
                label={`Buscar en ${spec.label.toLowerCase()}`}
                value={query}
                onChange={(e: any) => setQuery(e.target.value)}
                disabled={busy}
            />

            {query.trim().length < MIN_QUERY && (
                <Typography variant="body2" color="secondary">
                    Escribe al menos {MIN_QUERY} letras.
                </Typography>
            )}

            {searching && <Typography variant="body2" color="secondary">Buscando…</Typography>}

            {!searching && query.trim().length >= MIN_QUERY && results.length === 0 && (
                <Typography variant="body2" color="secondary">
                    {kind === "talent"
                        ? "Nadie con ese nombre. Si es una persona nueva, créala arriba."
                        : "Nada con ese nombre. Prueba con el apellido, o con el nombre de la empresa."}
                </Typography>
            )}

            {/*
                The employer nobody has heard of.

                A company that was never scraped and never registered has no row
                in any of the three tables searched above, and until now that
                made them impossible to record — which is backwards, because an
                employer who writes to us first is the best lead this business
                gets. Their own domain is the evidence, and it is offered rather
                than assumed: free mailbox providers are excluded, so this
                appears only when the address actually belongs to a company.
            */}
            {kind === "prospect" && fromAddress && !results.some(r => r.employerKey === fromAddress.key) && (
                <div className="flex flex-col gap-1.5">
                    <Typography variant="body2" color="secondary">
                        ¿No está en ninguna lista? Escribieron desde {fromAddress.website.replace(/^https?:\/\//, "")}.
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={() => link({
                            id: fromAddress.key,
                            name: fromAddress.name,
                            employerKey: fromAddress.key,
                            website: fromAddress.website,
                            hasRow: false
                        })}
                    >
                        Crear "{fromAddress.name}" y conectarla
                    </Button>
                </div>
            )}

            <div className="flex flex-col gap-1">
                {results.map(choice => (
                    <Card
                        key={choice.id}
                        onClick={() => !busy && link(choice)}
                        className={cls("p-2 flex flex-col", cardClickableMixin)}
                    >
                        <Typography variant="body2" className="truncate">{choice.name}</Typography>
                        {choice.detail && (
                            <Typography variant="body2" color="secondary" className="truncate">
                                {choice.detail}
                            </Typography>
                        )}
                    </Card>
                ))}
            </div>

            {error && <Alert color="error" size="small">{error}</Alert>}

            <div className="flex gap-2 items-center">
                <Button size="small" variant="text" onClick={onCancel} disabled={busy}>Cancelar</Button>
                {(thread.entity_type ?? "none") !== "none" && (
                    <Button size="small" variant="text" onClick={unlink} disabled={busy}>
                        Desconectar de esta ficha
                    </Button>
                )}
            </div>
        </Card>
    );
}
